import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { handlePostLoginActions } from '@/utils/postLoginHandler';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Global listener that catches Supabase auth events, handling routing
 * for password recovery and standard sign-ins.
 * This component now works alongside AuthContext to handle specific routing logic.
 */
const PasswordRecoveryHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, status } = useAuth();
  
  // Use a ref to track if a password recovery is in progress.
  // This prevents the SIGNED_IN event from redirecting away from the reset page.
  const isRecoveringPassword = useRef(false);

  useEffect(() => {
    // Log current URL and parameters for debugging
    console.log('[PasswordRecoveryHandler] Current URL:', window.location.href);
    console.log('[PasswordRecoveryHandler] URL search params:', window.location.search);
    console.log('[PasswordRecoveryHandler] URL hash:', window.location.hash);
    
    // Manual detection of password recovery flow as fallback
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(location.hash.substring(1));
    
    // Check for both 'code' and 'token' parameters (Supabase uses 'token' for PKCE flows)
    const hasRecoveryCode = urlParams.has('code') || hashParams.has('code');
    const hasRecoveryToken = urlParams.has('token') || hashParams.has('token');
    const hasRecoveryType = (urlParams.has('type') && urlParams.get('type') === 'recovery') || 
                           (hashParams.has('type') && hashParams.get('type') === 'recovery');
    const isRecoveryFlow = hasRecoveryCode || hasRecoveryToken || hasRecoveryType;
    
    console.log('[PasswordRecoveryHandler] Manual recovery detection:', { 
      hasRecoveryCode, 
      hasRecoveryToken, 
      hasRecoveryType, 
      isRecoveryFlow,
      searchParams: Array.from(urlParams.keys()),
      hashParams: Array.from(hashParams.keys()),
      currentPath: location.pathname
    });
    
    // If we detect recovery parameters in the URL, set the flag immediately
    if (isRecoveryFlow) {
      console.log('[PasswordRecoveryHandler] Recovery parameters detected in URL - setting flag');
      isRecoveringPassword.current = true;
    }
    
    // Only listen for PASSWORD_RECOVERY events to set the recovery flag
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[PasswordRecoveryHandler] Auth event:', event, 'Session:', !!session, 'Path:', location.pathname, 'Flag:', isRecoveringPassword.current);
      
      // The PASSWORD_RECOVERY event is the highest priority.
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[PasswordRecoveryHandler] PASSWORD_RECOVERY detected - setting flag');
        isRecoveringPassword.current = true;
        return;
      }

      // Manual fallback: If we detect recovery parameters in URL and are on reset page
      if (event === 'SIGNED_IN' && session && isRecoveryFlow && location.pathname === ROUTES.RESET_PASSWORD) {
        console.log('[PasswordRecoveryHandler] Manual recovery detection - treating SIGNED_IN as PASSWORD_RECOVERY');
        isRecoveringPassword.current = true;
        return;
      }

      // Additional fallback: If we're on the reset password page and have recovery parameters, set the flag
      if (location.pathname === ROUTES.RESET_PASSWORD && isRecoveryFlow) {
        console.log('[PasswordRecoveryHandler] On reset password page with recovery parameters - setting flag');
        isRecoveringPassword.current = true;
      }

      // Clear the flag when the user explicitly signs out (but not during recovery flow)
      if (event === 'SIGNED_OUT') {
        console.log('[PasswordRecoveryHandler] SIGNED_OUT detected');
        
        // Don't clear the flag immediately if we're in a recovery flow
        // PKCE flows can cause temporary SIGNED_OUT events
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasRecoveryCode = urlParams.has('code') || hashParams.has('code');
        const hasRecoveryToken = urlParams.has('token') || hashParams.has('token');
        const hasRecoveryType = (urlParams.has('type') && urlParams.get('type') === 'recovery') || 
                               (hashParams.has('type') && hashParams.get('type') === 'recovery');
        const isInRecoveryFlow = hasRecoveryCode || hasRecoveryToken || hasRecoveryType;
        
        if (isInRecoveryFlow || isRecoveringPassword.current) {
          console.log('[PasswordRecoveryHandler] SIGNED_OUT during recovery flow - keeping flag set');
          // Keep the flag set during recovery flow
        } else {
          console.log('[PasswordRecoveryHandler] SIGNED_OUT - clearing flag');
          isRecoveringPassword.current = false;
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [location]);

  // Clear the flag when the user successfully resets their password and navigates away
  // Only clear when they go to a different section of the app, not during the reset flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hasRecoveryCode = urlParams.has('code') || (urlParams.has('type') && urlParams.get('type') === 'recovery');
    const hasRecoveryHash = hashParams.has('type') && hashParams.get('type') === 'recovery';
    const isRecoveryFlow = hasRecoveryCode || hasRecoveryHash;
    
    if (isRecoveringPassword.current && 
        location.pathname !== ROUTES.RESET_PASSWORD && 
        location.pathname !== ROUTES.LOGIN &&
        !isRecoveryFlow) {
      console.log('[PasswordRecoveryHandler] User navigated away from reset flow - clearing flag');
      isRecoveringPassword.current = false;
    }
  }, [location.pathname]);

  // Handle post-login actions when user is authenticated and not in recovery flow
  useEffect(() => {
    if (status === 'authenticated' && user && !isRecoveringPassword.current) {
      // Only handle post-login actions if we're not in a recovery flow
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const hasRecoveryCode = urlParams.has('code') || hashParams.has('code');
      const hasRecoveryToken = urlParams.has('token') || hashParams.has('token');
      const hasRecoveryType = (urlParams.has('type') && urlParams.get('type') === 'recovery') || 
                             (hashParams.has('type') && hashParams.get('type') === 'recovery');
      const isRecoveryFlow = hasRecoveryCode || hasRecoveryToken || hasRecoveryType;
      
      if (!isRecoveryFlow && location.pathname === ROUTES.LOGIN) {
        console.log('[PasswordRecoveryHandler] User authenticated and not in recovery flow - handling post-login actions');
        handlePostLoginActions(navigate);
      }
    }
  }, [status, user, navigate, location.pathname, location.search, location.hash]);

  return null; // This component renders nothing.
};

export default PasswordRecoveryHandler; 